import { ArrowDropDown, CalendarMonthOutlined } from '@mui/icons-material';
import { Button, DialogActions, ListSubheader, Popover } from '@mui/material';
import {
  DateRangePickerDay,
  DateRangePickerDayProps,
  DateRangeValidationError,
  PickerChangeHandlerContext,
  PickersActionBarProps,
  PickersShortcutsItem,
  StaticDateRangePicker,
} from '@mui/x-date-pickers-pro';
import { t } from 'i18next';
import moment, { Moment } from 'moment';
import { useId, useMemo, useRef, useState } from 'react';
import { DateRange } from '../../../../../common/dashboards/types';
import { DateUtils } from '../../../../../common/utils/DateUtils';

/**
 * Internal formatter for the date range. It handles potential context from the DateRangePicker
 * to setup a label instead of date range.
 */
export const formatDateRangeLabel = ([startDate, endDate]: DateRange, context: PickerChangeHandlerContext<DateRangeValidationError> | null = null): string => {
  // If we picked a shortcut, use that label
  if (context !== null && context.shortcut !== undefined) {
    return context.shortcut.label;
  }

  return `${DateUtils.toDateString(startDate)} - ${DateUtils.toDateString(endDate)}`;
};

/**
 * Internal component which wraps the custom labels for the different action types.
 */
const ActionBar = (props: PickersActionBarProps) => {
  const { onAccept, onClear, onCancel, onSetToday, actions, className } = props;
  if (actions == null || actions.length === 0) {
    return null;
  }

  const menuItems = actions?.map((actionType) => {
    const buttonProps = {
      size: 'small',
    } as const;

    switch (actionType) {
      case 'clear':
        return (
          <Button {...buttonProps} sx={{ color: 'text.secondary' }} onClick={onClear} key={actionType}>
            {t('reset')}
          </Button>
        );
      case 'cancel':
        return (
          <Button {...buttonProps} onClick={onCancel} key={actionType}>
            {t('cancel')}
          </Button>
        );
      case 'accept':
        return (
          <Button {...buttonProps} onClick={onAccept} key={actionType}>
            {t('apply')}
          </Button>
        );
      case 'today':
        return (
          <Button {...buttonProps} onClick={onSetToday} key={actionType}>
            {t('today')}
          </Button>
        );
      default:
        return null;
    }
  });

  return (
    <DialogActions className={className} sx={{ justifyContent: 'space-between', flexDirection: 'row-reverse' }}>
      {menuItems}
    </DialogActions>
  );
};

type Props = {
  minDate?: string;
  onChange: (value: { label: string; range: DateRange }) => void;
  value: DateRange;
  label: string;
  disabled?: boolean;
};

/**
 * Wrapper for the day element to deal with accessibility elements of button.
 * Default role applied is `gridcell`, and buttons cannot have that role since they shouldn't contain
 * other interactive elements inside itself.
 */
const WrapperDateRangePickerDay = (props: DateRangePickerDayProps<Moment> & React.RefAttributes<HTMLButtonElement>) => {
  const { 'aria-colindex': ariaColIndex, 'aria-current': ariaCurrent, 'aria-selected': ariaSelected, role, ...otherProps } = props;

  return (
    <span aria-colindex={ariaColIndex} aria-current={ariaCurrent} aria-selected={ariaSelected} role={role}>
      <DateRangePickerDay {...otherProps} aria-label={props.day.format('LL')} />
    </span>
  );
};

const MIN_DATE = moment().subtract(1, 'year').startOf('day').toISOString();

/**
 * Main component for the date range functionality.
 */
export const DateRangePicker = ({ minDate = MIN_DATE, value, onChange, label, disabled = false }: Props) => {
  const id = useId();
  // We use this to track context changes between picking a shortcut option and applying changes.
  const buttonLabelRef = useRef<PickerChangeHandlerContext<DateRangeValidationError> | null>({
    validationError: [null, null],
    shortcut: { label },
  });
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const anchorEl = useRef<HTMLButtonElement | null>(null);

  const minMoment = useMemo(() => moment(minDate), [minDate]);
  const SHORTCUT_ITEMS = useMemo<PickersShortcutsItem<DateRange>[]>(() => {
    return [
      {
        label: t('advancedFilters.shortcuts.today'),
        getValue: () => {
          const now = moment();
          const start = now.clone().startOf('day');
          const end = now.clone().endOf('day');
          return [start, end];
        },
      },
      {
        label: t('advancedFilters.shortcuts.yesterday'),
        getValue: () => {
          const yesterday = moment().subtract(1, 'day');
          const start = yesterday.clone().startOf('day');
          const end = yesterday.clone().endOf('day');
          return [start, end];
        },
      },
      {
        label: t('advancedFilters.shortcuts.past7Days'),
        getValue: () => {
          const start = moment().subtract(7, 'days').startOf('day');
          const end = moment().endOf('day');
          return [start, end];
        },
      },
      {
        label: t('advancedFilters.shortcuts.past30Days'),
        getValue: () => {
          const start = moment().subtract(30, 'days').startOf('day');
          const end = moment().endOf('day');
          return [start, end];
        },
      },
      {
        label: t('advancedFilters.shortcuts.past90Days'),
        getValue: () => {
          const start = moment().subtract(90, 'days').startOf('day');
          const end = moment().endOf('day');
          return [start, end];
        },
      },
      {
        label: t('advancedFilters.shortcuts.pastYear'),
        getValue: () => {
          const start = moment().subtract(1, 'year').startOf('day');
          const end = moment().endOf('day');
          return [start, end];
        },
      },
      {
        label: t('advancedFilters.shortcuts.allTime'),
        getValue: () => {
          const start = minMoment.startOf('day');
          const end = moment().endOf('day');
          return [start, end];
        },
      },
    ];
  }, [minMoment]);

  const mainButtonVariant = datePickerOpen ? 'contained' : 'outlined';

  return (
    <>
      <Button
        id={id}
        ref={anchorEl}
        variant={mainButtonVariant}
        startIcon={<CalendarMonthOutlined />}
        endIcon={<ArrowDropDown />}
        onClick={() => setDatePickerOpen(true)}
        disabled={disabled}
      >
        {label}
      </Button>
      <Popover
        open={datePickerOpen}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        role="dialog"
        aria-labelledby={id}
        anchorEl={anchorEl.current}
        onClose={() => {
          setDatePickerOpen(false);
        }}
      >
        <StaticDateRangePicker
          calendars={2}
          onChange={(dateRange, context) => {
            // Save the last context so that we can format the actual value on the apply part
            buttonLabelRef.current = context;
          }}
          disableFuture={true}
          minDate={minMoment}
          value={value}
          onAccept={(dateRange) => {
            // Close datepicker and change the label of the button
            setDatePickerOpen(false);

            const buttonLabelContext = buttonLabelRef.current;
            buttonLabelRef.current = null;

            const [startDate, endDate] = dateRange;

            // We don't allow nullable states, we always need to have full range
            if (startDate === null || endDate === null) {
              return;
            }

            const label = formatDateRangeLabel([startDate, endDate], buttonLabelContext);

            // Provided dates should reflect the start/end of day, so that in case we select today
            // through date picker (not shortcut), we actually get today's results.
            const range = [startDate.startOf('day'), endDate.endOf('day')] as DateRange;

            onChange({ label, range });
          }}
          slots={{
            actionBar: ActionBar,
            day: WrapperDateRangePickerDay,
          }}
          sx={{
            '.MuiPickersToolbar-root': {
              display: 'none',
            },
          }}
          slotProps={{
            shortcuts: {
              items: SHORTCUT_ITEMS,
              dense: true,
              subheader: <ListSubheader>{t('advancedFilters.timePresets')}</ListSubheader>,
            },
            actionBar: {
              actions: ['accept'],
            },
          }}
        />
      </Popover>
    </>
  );
};
